import { create } from 'zustand'

const storageKey = (userId: string) => `onboarding-skipped:${userId}`

interface OnboardingSkipState {
  /** Per-user skip flag, mirrored from localStorage and kept reactive. */
  skipped: Record<string, boolean>
  hydrate: (userId: string) => void
  markSkipped: (userId: string) => void
  clearSkip: (userId: string) => void
}

/**
 * Reactive store for the "skipped onboarding" flag. Backed by localStorage for
 * persistence across reloads, but exposed through zustand so the forced gate and
 * the dialog (separate hook instances) react to a skip immediately.
 */
export const useOnboardingSkipStore = create<OnboardingSkipState>(
  (set, get) => ({
    skipped: {},
    hydrate: (userId) => {
      if (typeof window === 'undefined') {
        return
      }
      const value = window.localStorage.getItem(storageKey(userId)) === 'true'
      if (get().skipped[userId] !== value) {
        set((state) => ({ skipped: { ...state.skipped, [userId]: value } }))
      }
    },
    markSkipped: (userId) => {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey(userId), 'true')
      }
      set((state) => ({ skipped: { ...state.skipped, [userId]: true } }))
    },
    clearSkip: (userId) => {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(storageKey(userId))
      }
      set((state) => ({ skipped: { ...state.skipped, [userId]: false } }))
    },
  })
)
